import type { BcfTopic, OrigemDoTopico } from './bcfParser'
import { CAMPOS_EDITAVEIS } from './bcfSync'

/** A origem de um tópico: como veio do ficheiro importado.
 *
 *  O problema que isto resolve apareceu no ensaio de 15/10/2026. O João
 *  e o Rui importaram o mesmo `.bcfzip`. O João sincronizou, mudou o
 *  estado de um tópico de Open para Active e sincronizou outra vez. O
 *  Rui sincronizou pela primeira vez e recebeu um conflito no estado:
 *  Open cá, Active lá, e sem base nenhuma, porque nunca tinha
 *  sincronizado. Sem base não se sabe quem mudou, e a regra é não
 *  adivinhar.
 *
 *  Mas havia uma base, e era óbvia: o ficheiro. Os dois tinham o tópico
 *  como o ficheiro o trazia, Open; quem mudou foi o João. A origem é
 *  essa fotografia, guardada no próprio tópico no momento da importação
 *  e imutável a partir daí.
 *
 *  **Quando é que a origem serve de base.** Só quando há razões para
 *  crer que os dois lados partiram dela:
 *
 *  - os dois tópicos trazem origem, e é a mesma: o mesmo GUID, com o
 *    mesmo conteúdo de origem. É o caso normal a partir desta versão;
 *  - o tópico de cá traz origem e o do servidor não: foi enviado por uma
 *    versão anterior, que não a mandava. O GUID e o BCF são os mesmos, e
 *    é o melhor que se sabe. O risco (o outro lado ter partido de outra
 *    versão do ficheiro, com o mesmo GUID e outro valor) fica escrito em
 *    docs/BCF_SINCRONIZACAO.md.
 *
 *  Quando as duas origens existem e diferem, os dois partiram de
 *  ficheiros diferentes: não há base, e um campo que difira é conflito,
 *  como antes. Quando o tópico de cá não tem origem, também não.
 *
 *  A base da sincronização, quando existe, ganha sempre: é um acordo
 *  mais recente do que o ficheiro.
 *
 *  Puro, sem store nem rede.
 */

const CONJUNTOS = ['labels', 'watchers', 'viewpointRefs', 'relatedTopics'] as const

function lista(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
}

function valor(v: unknown): string | null {
  return typeof v === 'string' ? v : v == null ? null : String(v)
}

/** A fotografia de origem de um tópico, tal como está agora. */
export function origemDe(t: BcfTopic): OrigemDoTopico {
  const campos: Record<string, string | null> = {}
  for (const c of CAMPOS_EDITAVEIS) campos[c] = valor(t[c])
  return {
    campos,
    labels: [...t.labels],
    watchers: [...t.watchers],
    viewpointRefs: [...t.viewpointRefs],
    relatedTopics: lista(t.relatedTopics),
  }
}

/** Os tópicos de um ficheiro acabado de importar, cada um com a sua
 *  origem. Um tópico que já a traga (veio de uma sessão exportada)
 *  mantém-na. */
export function comOrigem(topicos: readonly BcfTopic[]): BcfTopic[] {
  return topicos.map((t) => (t.origem ? t : { ...t, origem: origemDe(t) }))
}

/**
 * Dá origem aos tópicos de um BCF importado antes de a origem existir,
 * quando se pode afirmar que estão como vieram do ficheiro.
 *
 * Cada edição local escreve a data de modificação do momento (ver
 * `bcfClientSlice`), e esse momento é sempre posterior à importação. Um
 * tópico cuja data de modificação (ou de criação, sem modificação) não
 * passa do momento da importação nunca foi editado cá: está como o
 * ficheiro o trouxe. Os outros ficam sem origem; não se inventa.
 */
export function origemRetroactiva(
  topicos: readonly BcfTopic[],
  importadoEm: number,
): BcfTopic[] {
  let mudou = false
  const fora = topicos.map((t) => {
    if (t.origem) return t
    const data = Date.parse(t.modifiedDate ?? t.creationDate ?? '')
    if (!Number.isFinite(data) || data > importadoEm) return t
    mudou = true
    return { ...t, origem: origemDe(t) }
  })
  return mudou ? fora : (topicos as BcfTopic[])
}

function chave(o: OrigemDoTopico): string {
  const campos = CAMPOS_EDITAVEIS.map((c) => [c, o.campos?.[c] ?? null])
  const conjuntos = CONJUNTOS.map((c) => [c, [...lista(o[c])].sort()])
  return JSON.stringify([campos, conjuntos])
}

/** As duas origens são a mesma? Pelos valores, e os conjuntos sem
 *  ordem. */
export function mesmaOrigem(a: OrigemDoTopico, b: OrigemDoTopico): boolean {
  return chave(a) === chave(b)
}

/** Uma origem que venha do servidor tem a forma certa? O que vem de fora
 *  é dado e valida-se antes de servir de base. */
function origemValida(o: unknown): o is OrigemDoTopico {
  if (!o || typeof o !== 'object') return false
  const r = o as Record<string, unknown>
  return !!r.campos && typeof r.campos === 'object' && CONJUNTOS.every((c) => Array.isArray(r[c]))
}

/**
 * A base que os dois lados partilham por terem partido do mesmo
 * ficheiro, como um tópico, ou null se não há razões para crer que
 * partiram do mesmo sítio (ver o cabeçalho).
 */
export function baseDaOrigem(meu: BcfTopic, deles: BcfTopic): BcfTopic | null {
  const minha = meu.origem
  if (!origemValida(minha)) return null
  const delesOrigem = (deles as { origem?: unknown }).origem
  if (delesOrigem !== undefined && delesOrigem !== null) {
    if (!origemValida(delesOrigem) || !mesmaOrigem(minha, delesOrigem)) return null
  }
  const base: BcfTopic = {
    ...meu,
    labels: lista(minha.labels),
    watchers: lista(minha.watchers),
    viewpointRefs: lista(minha.viewpointRefs),
    relatedTopics: lista(minha.relatedTopics),
    comments: [],
  }
  delete base.commentsApagados
  for (const c of CAMPOS_EDITAVEIS) {
    ;(base as unknown as Record<string, unknown>)[c] = minha.campos[c] ?? null
  }
  return base
}
